/**
 * Permission-Protected Route Component
 * 
 * Wraps routes that require specific permissions and redirects users
 * who don't have access to the appropriate section.
 */

import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Shield, Server } from "lucide-react";

// Define which permissions are required for each route pattern
const ROUTE_PERMISSIONS = {
  // Apps Management routes require view_apps
  "/apps": "view_apps",
  "/discover": "view_apps",
  "/packages": "view_apps",
  "/deployments": "view_apps",
  "/catalog": "view_apps",
  
  // OS Patch Management routes require view_os_patches
  "/os-patch": "view_os_patches",
  
  // Dashboard is viewable by all authenticated users
  "/dashboard": "view_dashboard",
  
  // Settings is viewable by all authenticated users
  "/settings": "view_settings",
};

// Routes that need a capability the tenant's ONBOARDING MODE may not
// grant. This is a separate axis from the permissions above: the user's
// role may be fine while the tenant simply has no Intune connection.
// Longest prefix wins, so /os-patch does not shadow a more specific entry.
const ROUTE_CAPABILITIES = {
  "/deployments": "intune_deployment",
  "/os-patch": "os_patch_management",
  // Not in the reporting-only OS Patch plan. /os-patch/groups stays open:
  // its Firmware Deployments tab is part of that plan.
  "/os-patch/updates": "os_patch_policies",
  "/os-patch/remediation": "os_patch_policies",
};

const getRequiredCapability = (path) => {
  let match = null;
  for (const [pattern, capability] of Object.entries(ROUTE_CAPABILITIES)) {
    if (path.startsWith(pattern) && (!match || pattern.length > match[0].length)) {
      match = [pattern, capability];
    }
  }
  return match ? match[1] : null;
};

// Get required permission for a path
const getRequiredPermission = (path) => {
  for (const [pattern, permission] of Object.entries(ROUTE_PERMISSIONS)) {
    if (path.startsWith(pattern)) {
      return permission;
    }
  }
  return null; // No specific permission required
};

// Get redirect path based on available permissions
const getRedirectPath = (permissions) => {
  if (!permissions || permissions.length === 0) {
    return "/dashboard";
  }
  
  // Try to redirect to the most appropriate section
  if (permissions.includes("view_apps")) {
    return "/discover";
  }
  if (permissions.includes("view_os_patches")) {
    return "/os-patch/dashboard";
  }
  return "/dashboard";
};

export const PermissionProtectedRoute = ({ children, requiredPermission }) => {
  const { demoMode, getAccessToken } = useAuth();
  const location = useLocation();
  const [permissions, setPermissions] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [connectionType, setConnectionType] = useState("intune");
  // Service tier from /users/me; null when an older backend doesn't report it.
  const [serviceTier, setServiceTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine required permission from route if not explicitly provided
  const permission = requiredPermission || getRequiredPermission(location.pathname);
  const capability = getRequiredCapability(location.pathname);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (demoMode) {
        // Demo mode: full access
        setPermissions([
          "view_apps", "manage_apps", "create_packages", "deploy_packages",
          "view_os_patches", "manage_os_patches", "view_settings", "manage_settings",
          "manage_users", "view_users", "view_dashboard"
        ]);
        setCapabilities(null);   // demo mode: full Intune capability set
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setPermissions(response.data.permissions || []);
        // null = unknown (older backend) -> permissive, matching the
        // backend guard, which is the authoritative check either way.
        setCapabilities(
          Array.isArray(response.data.capabilities) ? response.data.capabilities : null
        );
        setConnectionType(response.data.connection_type || "intune");
        setServiceTier(response.data.service_tier || null);
      } catch (err) {
        console.error("Failed to fetch permissions:", err);
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) {
          // Genuine auth/permission response — keep the minimal fallback.
          setPermissions(["view_dashboard"]);
        } else {
          // 2026-07-30 incident hardening: 5xx / network failure means the
          // BACKEND is unhealthy — masquerading it as "Access Denied"
          // (the old view_dashboard fallback) made a bad deploy look like
          // an RBAC misconfiguration on every tenant. Surface a truthful
          // "service unavailable" state instead.
          setError(err);
          setPermissions(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [demoMode, getAccessToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2847 50%, #1a1a4e 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4" />
          <p className="text-white/60">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Backend unreachable / 5xx while fetching permissions — truthful
  // outage state instead of a misleading "Access Denied".
  if (error && permissions === null) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2847 50%, #1a1a4e 100%)' }}>
        <div className="text-center max-w-md p-8 bg-white/5 rounded-xl border border-white/10" data-testid="service-unavailable-card">
          <Shield className="h-16 w-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Service Temporarily Unavailable</h2>
          <p className="text-white/60 mb-6">
            We couldn't reach the ALICE service to verify your access. This is
            usually a temporary backend issue — not a permissions problem.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-block px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            data-testid="service-unavailable-retry"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Capability gate -- the tenant's onboarding mode, not the user's role.
  // Checked BEFORE permissions: when both would fire, "your tenant has no
  // Intune connection" is the accurate explanation, not "access denied".
  // os_patch_policies is only enforced when the backend reports a service
  // tier: an older backend never lists it and must not lose these pages.
  const capabilityMissing =
    capability &&
    capabilities &&
    !capabilities.includes(capability) &&
    !(capability === "os_patch_policies" && !serviceTier);
  if (capabilityMissing && capability === "os_patch_policies") {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2847 50%, #1a1a4e 100%)' }}>
        <div className="text-center max-w-md p-8 bg-white/5 rounded-xl border border-white/10" data-testid="plan-unavailable-card">
          <Server className="h-16 w-16 text-sky-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Not included in your plan</h2>
          <p className="text-white/60 mb-4">
            Update rings, feature updates, expedited updates and remediation
            scripts aren&apos;t part of your organisation&apos;s ALICE plan.
            OS patch compliance reporting is available from Discover.
          </p>
          <a
            href="/os-patch/discover"
            className="inline-block px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go to OS Patch Discover
          </a>
        </div>
      </div>
    );
  }
  if (capabilityMissing) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2847 50%, #1a1a4e 100%)' }}>
        <div className="text-center max-w-md p-8 bg-white/5 rounded-xl border border-white/10" data-testid="capability-unavailable-card">
          <Server className="h-16 w-16 text-sky-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Not available for this tenant</h2>
          <p className="text-white/60 mb-4">
            {connectionType === "sccm"
              ? "This tenant was onboarded from an SCCM import and has no Intune connection. Application discovery, rationalisation and packaging are available; Intune deployment and OS Patch Management are not."
              : "This section is not available for this tenant's connection type."}
          </p>
          <a
            href="/discover"
            className="inline-block px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go to Discover
          </a>
        </div>
      </div>
    );
  }

  // If a specific permission is required and user doesn't have it
  if (permission && permissions && !permissions.includes(permission)) {
    // Show access denied message
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0d2847 50%, #1a1a4e 100%)' }}>
        <div className="text-center max-w-md p-8 bg-white/5 rounded-xl border border-white/10">
          <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-white/60 mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-white/40 text-sm mb-6">
            Required permission: <code className="bg-white/10 px-2 py-1 rounded">{permission}</code>
          </p>
          <a 
            href={getRedirectPath(permissions)} 
            className="inline-block px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default PermissionProtectedRoute;
